import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { content } from "@/lib/content";
import { OG_IMAGE, absolute, hreflangLinks } from "@/lib/site";
import { graph, serviceSchema, breadcrumbList, faqPage } from "@/lib/schema";
import { SERVICE_TYPE, areaServedFor, titleWithLabel, META_DESCRIPTION } from "@/lib/services-meta";

export const Route = createFileRoute("/services/$slug")({
  beforeLoad: ({ params }) => {
    if (!content.services.items.find((i) => i.slug === params.slug)) throw notFound();
  },
  head: ({ params }) => {
    const item = content.services.items.find((i) => i.slug === params.slug);
    if (!item) return {};
    const title = item
      ? `${titleWithLabel(item.label, item.title.en, "en")} — IraqCert`
      : "ISO Service — IraqCert";
    const description = item
      ? META_DESCRIPTION[item.slug]?.en ?? item.heroSubtitle.en
      : "ISO certification services by IraqCert.";
    const url = absolute(`/services/${params.slug}`);
    const image = OG_IMAGE;

    const scripts = item
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify(
              graph(
                serviceSchema({
                  path: `/services/${item.slug}`,
                  name: titleWithLabel(item.label, item.title.en, "en"),
                  serviceType: SERVICE_TYPE[item.slug] ?? item.title.en,
                  description,
                  areaServed: areaServedFor(item.slug),
                }),
                breadcrumbList([
                  { name: "Home", path: "/" },
                  { name: "Services", path: "/services" },
                  { name: item.label, path: `/services/${item.slug}` },
                ]),
                ...(content.faq.services[item.slug]?.length
                  ? [
                      faqPage(
                        `/services/${item.slug}`,
                        content.faq.services[item.slug].map((f) => ({
                          q: f.q.en,
                          a: f.a.en,
                        })),
                      ),
                    ]
                  : []),
              ),
            ),
          },
        ]
      : [];

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:image", content: image },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/services/${params.slug}`),
      ],
      scripts,
    };
  },
  component: ServiceDetailPage,
});

function ServiceDetailPage() {
  const { slug } = Route.useParams();
  const item = content.services.items.find((i) => i.slug === slug);

  if (!item) throw notFound();

  return <ServiceDetail item={item} />;
}
